import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useWatch } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Control } from 'react-hook-form/dist/types/form';
import { Box, Button, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { formatCurrency, formatPercentage } from 'utils';
import { CreditSubmission, Proposal, VendorOpportunity } from '@trnsact/trnsact-shared-types';
import { LenderApproval } from 'components/CompareApprovals/types';
import { InputField } from 'components/form/rhf/InputField';
import { ApprovalFormValues, ApprovalNotesFormValues } from '../../types';
import { Decision, DECISIONS } from '../../constants';
import { SectionTitle } from './SectionTitle';
import { SubSectionTitle } from './SubSectionTitle';
import { ApprovalTermRow } from './ApprovalTermRow';
import { EditApprovalOverviewDialog } from '../dialogs';

type SaveFieldOnBlur = (
  fieldName: 'transactionSummary' | 'lenderNotes' | 'internalNotes' | 'stage',
  value: string | number | null | undefined,
  successMessage: string
) => Promise<void>;

interface Props {
  creditSubmission: CreditSubmission & { proposals?: Proposal[]; contingencies: string[] };
  proposal?: Proposal | null;
  vo: VendorOpportunity;
  approval: LenderApproval | null;
  isLoading?: boolean;
  formControl: Control<ApprovalNotesFormValues>;
  decisionStatus: Decision | string | null | undefined;
  onSaveField: SaveFieldOnBlur;
  onSaveOverview?: (approvalValues: ApprovalFormValues, conditions: string[]) => Promise<void>;
}

export const ApprovalOverviewCard = ({
  creditSubmission,
  proposal,
  vo,
  approval,
  isLoading,
  formControl: control,
  decisionStatus,
  onSaveField,
  onSaveOverview,
}: Props) => {
  const history = useHistory();
  const { handleOpen: handleOpenEditOverview } = useModal(ModalsKeys.EditApprovalOverviewDialog);

  const classes = useStyles();

  const transactionSummary = useWatch({ control, name: 'transactionSummary' });
  const lenderNotes = useWatch({ control, name: 'lenderNotes' });
  const internalNotes = useWatch({ control, name: 'internalNotes' });

  const parsedDecisionId = approval?.parsedDecisionId;
  const isAwaitingReview = decisionStatus === DECISIONS.AWAITING_REVIEW || !decisionStatus;
  const isRequestedInfo = decisionStatus === DECISIONS.REQUESTED_ADDITIONAL_INFO;
  const isDeclined = decisionStatus === DECISIONS.DECLINED;
  const hasNoApprovalTerms = isAwaitingReview || isRequestedInfo || isDeclined;

  const handleCreateProposal = () => {
    if (!proposal?.proposalId) return;
    history.push({
      hash: '#desking',
      pathname: `/vo/${vo.vendorOpportunityId}`,
      state: { proposalId: proposal.proposalId },
    });
  };

  const proposalFinance = proposal?.createdSnapshot?.paymentOptions?.[0]?.finance;
  const pricing = proposal?.createdSnapshot?.settings?.configuration?.pricing;
  const approvalTerms = approval?.approvalTerms;
  const approvedAmount = approvalTerms?.approvedAmount ?? creditSubmission.approvedAmount;
  const docFeeTotal = (approvalTerms?.lenderFees ?? [])
    .filter((f: any) => f?.feeName?.toLowerCase()?.includes('doc'))
    .reduce((sum: number, f: any) => sum + (f?.amount ?? 0), 0);

  return (
    <Paper elevation={0} className={classes.root}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2, alignItems: 'center' }}>
        <SectionTitle>Submission Overview</SectionTitle>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {!hasNoApprovalTerms && (
            <Button
              size="small"
              variant="outlined"
              sx={{ justifySelf: 'flex-end' }}
              startIcon={<EditIcon />}
              onClick={() =>
                handleOpenEditOverview({
                  creditSubmission,
                  approvalTerms,
                  docFeeTotal,
                  parsedDecisionId: parsedDecisionId ?? null,
                  onSave: async (approvalValues, conditions) => onSaveOverview?.(approvalValues, conditions),
                })
              }
            >
              Edit Approval Terms
            </Button>
          )}
        </Box>

        <Stack direction="row" gap={1} justifyContent="space-between">
          <Box className={classes.amountCardsContainer}>
            <Typography variant="body2" color="text.secondary">
              Opportunity Amount
            </Typography>
            <Typography variant="h6">{formatCurrency(vo.invoiceTotal)}</Typography>
          </Box>

          <Box className={classes.amountCardsContainer}>
            <Typography variant="body2" color="text.secondary">
              Requested Amount
            </Typography>
            <Typography variant="h6">{formatCurrency(creditSubmission.amountRequested)}</Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
        <Stack gap={0.5}>
          <SubSectionTitle>Approval Terms</SubSectionTitle>
          {hasNoApprovalTerms ? (
            <Typography variant="subtitle2" color="text.secondary" component={'p'}>
              Approval terms will appear once the lender returns a decision.
            </Typography>
          ) : isLoading && !approval ? (
            <Stack gap={1}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="text" width="100%" height={24} />
              ))}
            </Stack>
          ) : (
            <>
              <ApprovalTermRow label="Approved Amount" value={approvedAmount ? formatCurrency(approvedAmount) : '—'} />
              <ApprovalTermRow label="Finance Program" value={proposalFinance?.financeProgramName || '—'} />
              <ApprovalTermRow
                label="Term"
                value={approvalTerms?.termLengthMonths ? String(approvalTerms.termLengthMonths) : '—'}
              />
              <ApprovalTermRow
                label="Interest Rate"
                value={approvalTerms?.interestRate ? formatPercentage(approvalTerms?.interestRate) : '—'}
              />
              <ApprovalTermRow
                label="Minimum Required Down Payment"
                value={
                  approvalTerms?.minimumRequiredDownPayment
                    ? formatPercentage(approvalTerms?.minimumRequiredDownPayment)
                    : '—'
                }
              />
              <ApprovalTermRow
                label="Days To First Payment"
                value={approvalTerms?.daysToFirstPayment != null ? String(approvalTerms.daysToFirstPayment) : '—'}
              />

              <ApprovalTermRow label="Doc Fees" value={docFeeTotal ? formatCurrency(docFeeTotal) : '—'} />
            </>
          )}
        </Stack>

        <Stack gap={0.5}>
          <SubSectionTitle>Payment Options</SubSectionTitle>
          {hasNoApprovalTerms ? (
            <Typography variant="subtitle2" color="text.secondary" component={'p'}>
              Financing options are not available yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
              {pricing &&
                Object.values(pricing).length > 0 &&
                Object.values(pricing).map((option: any) => (
                  <Box
                    key={option.term}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 130,
                      flex: '1 1 0',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <Typography variant="h5">
                      {option.paymentAmountPerPeriod != null ? formatCurrency(option.paymentAmountPerPeriod) : '—'}
                      /mo.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      for {option.term ?? '—'} months
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}
        </Stack>

        <Stack gap={0.5}>
          <SubSectionTitle>Conditions &amp; Stipulations</SubSectionTitle>
          {isAwaitingReview ? (
            <Typography variant="subtitle2" color="text.secondary" component={'p'}>
              No conditions or additional information requests at this time.
            </Typography>
          ) : (
            (approvalTerms?.conditions ?? creditSubmission.contingencies)?.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'rgba(0,0,0,0.12)',
                }}
              >
                <Typography variant="body1">{typeof item === 'string' ? item : item.description}</Typography>
              </Box>
            ))
          )}
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
        <Stack gap={2}>
          <InputField
            name="transactionSummary"
            label="Transaction Summary"
            control={control}
            inputProps={{
              variant: 'outlined',
              onBlur: () => onSaveField('transactionSummary', transactionSummary, 'Transaction Summary saved'),
            }}
          />
          <InputField
            name="lenderNotes"
            label="Notes from Lender"
            control={control}
            inputProps={{
              variant: 'outlined',
              multiline: true,
              rows: 3,
              //onBlur: () => onSaveField('lenderNotes', lenderNotes, 'Lender Notes saved'),
              disabled: true,
            }}
          />
          <InputField
            name="internalNotes"
            label="Internal Notes"
            control={control}
            inputProps={{
              variant: 'outlined',
              multiline: true,
              rows: 3,
              onBlur: () => onSaveField('internalNotes', internalNotes, 'Internal Notes saved'),
            }}
          />
        </Stack>
      </Box>

      <Divider />

      {!hasNoApprovalTerms && (
        <Box>
          <Typography variant="subtitle1" component="span" fontWeight={600}>
            Ready to proceed?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Create a quote using these approval terms.
          </Typography>
          <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={handleCreateProposal}>
            Create Proposal
          </Button>
        </Box>
      )}

      <EditApprovalOverviewDialog />
    </Paper>
  );
};

const useStyles = makeStyles({
  root: {
    boxShadow: '0px 2px 12px 0px rgba(0,0,0,0.1)',
    padding: '16px 24px',
    gap: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
  amountCardsContainer: {
    padding: '8px 16px',
    borderRadius: 1,
    backgroundColor: 'rgba(227, 242, 253, 0.7)',
    boxShadow: '0px 4px 8px -1px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: '8px',
  },
});
